import React, { useCallback, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditIcon from "@mui/icons-material/Edit";

import useQueryParams from "../../../../hooks/useQuery";
import Dimensions from "../../../../views/inventory/requests/Dimensions";
import AddComponentModal from "./AddComponent/AddComponentModal";
import { getImage } from "../../../../views/shopping/Cart/CartItemFields";
import { useKitAssets } from "../../../../contexts/kit-assets";
import ItemId from "../../../../views/shopping/Orders/ItemId";
import AssetDetailsModal from "src/views/inventory/requests/asset-details/AssetDetailsModal";
import useAuth from "../../../../hooks/useAuth";
import KitAssetQuantity from "./KitAssetQuantity";
import ConfirmDeleteModal from "../../../images/ConfirmDeleteModal";

export default function KitBuilder({ disabled, entity, projectId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const query = useQueryParams();
  const {
    kit,
    loading,
    removeKitAsset,
    updateKitAsset,
  } = useKitAssets();
  const [editMode, setEditMode] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  // Check if grouping by SKU is enabled
  const isGroupingEnabled = entity?.groupBySkuNumber || false;

  // Get the grouped kit assets data directly from the kit context
  const groupedKitAssets = useMemo(() => {
    if (!isGroupingEnabled) return [];

    if (kit?.groupedKitAssets?.length) {
      return kit.groupedKitAssets;
    }

    const groupedAssetsMap = new Map();

    (kit?.kitAssets || []).forEach(({ asset, quantity }) => {
      const skuNumber = asset?.skuNumber || asset?.sku_number;
      const description = asset?.description;

      if (!skuNumber || !description) return;

      const key = `${skuNumber}::${description}`;
      const existingGroup = groupedAssetsMap.get(key);

      if (existingGroup) {
        existingGroup.required_quantity += quantity;
        existingGroup.total_available_quantity += Number(asset?.availableQuantity || 0);
        if (asset?.itemId) existingGroup.associated_item_ids.push(asset.itemId);
        if (asset?.id) existingGroup.associated_asset_ids.push(asset.id);
        return;
      }

      groupedAssetsMap.set(key, {
        sku_number: skuNumber,
        description,
        required_quantity: quantity,
        total_available_quantity: Number(asset?.availableQuantity || 0),
        associated_item_ids: asset?.itemId ? [asset.itemId] : [],
        associated_asset_ids: asset?.id ? [asset.id] : [],
      });
    });

    return Array.from(groupedAssetsMap.values());
  }, [kit, isGroupingEnabled]);

  const hasModel = asset => {
    return asset?.model && asset.model.trim() !== "";
  };

  const hasDimensions = asset => {
    if (!asset?.dimensions || !Array.isArray(asset.dimensions) || asset.dimensions.length === 0) {
      return false;
    }

    return asset.dimensions.some(dim => {
      const width = parseFloat(dim.width) || 0;
      const depth = parseFloat(dim.depth) || 0;
      const height = parseFloat(dim.height) || 0;
      return width > 0 || depth > 0 || height > 0;
    });
  };

  // Determine if we should show these columns based on any asset having the data
  const showModelColumn = kit?.kitAssets?.some(({ asset }) => hasModel(asset)) || false;
  const showDimensionsColumn = kit?.kitAssets?.some(({ asset }) => hasDimensions(asset)) || false;

  const handleToggle = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;

    if (params.get("kitId")) {
      params.delete("kitId");
    } else {
      params.set("kitId", entity.id);
    }

    navigate({ pathname, search: params.toString() });
  }, [query, navigate, entity?.id]);

  const handleConfirmRemove = async () => {
    if (itemToRemove) {
      await removeKitAsset(itemToRemove);
    }
  };

  const handleCloseRemoveModal = () => {
    setItemToRemove(null);
  };

  // Render grouped view table (similar to GroupedAssetViewModal)
  const renderGroupedView = () => {
    if (!groupedKitAssets.length) {
      return (
        <Box p={3} bgcolor="#f4f6f8" borderRadius="4px" display="flex" justifyContent="center">
          <Typography variant="body2" color="textSecondary">
            No kit components with SKU numbers found for this kit
          </Typography>
        </Box>
      );
    }

    const findKitAssetForGrouped = groupedAsset => {
      return kit?.kitAssets?.find(ka => {
        const kitAssetSkuNumber = ka.asset.skuNumber || ka.asset.sku_number;
        const groupedSkuNumber = groupedAsset.sku_number || groupedAsset.skuNumber;

        if (!kitAssetSkuNumber) {
          return ka.asset.description === groupedAsset.description;
        }

        return (
          kitAssetSkuNumber === groupedSkuNumber &&
          ka.asset.description === groupedAsset.description
        );
      });
    };

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="center">SKU Number</TableCell>
            <TableCell align="center">Associated Item IDs</TableCell>
            <TableCell align="center">Description</TableCell>
            <TableCell align="center">Required Quantity</TableCell>
            <TableCell align="center">Total Available Quantity</TableCell>
            {!disabled && editMode && <TableCell align="center" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {groupedKitAssets.map((groupedAsset, index) => {
            const kitAsset = findKitAssetForGrouped(groupedAsset);
            const highlight =
              groupedAsset.required_quantity > groupedAsset.total_available_quantity;
            const background = highlight ? "#fffbf2" : undefined;

            return (
              <TableRow key={`${groupedAsset.sku_number}-${index}`} hover>
                <TableCell align="center" style={{ background }}>
                  {groupedAsset.sku_number}
                </TableCell>
                <TableCell align="center" style={{ background }}>
                  {groupedAsset.associated_item_ids && groupedAsset.associated_item_ids.length > 0
                    ? groupedAsset.associated_item_ids.map((itemId, idx) => (
                        <div key={idx}>
                          <AssetDetailsModal
                            asset={{
                              id: groupedAsset.associated_asset_ids[idx],
                              itemId: itemId,
                              project: { id: projectId },
                            }}
                          />
                        </div>
                      ))
                    : "—"}
                </TableCell>
                <TableCell align="center" style={{ background }}>
                  {groupedAsset.description}
                </TableCell>
                <TableCell align="center" style={{ background }}>
                  {kitAsset ? (
                    <KitAssetQuantity
                      disabled={disabled || !editMode}
                      value={kitAsset.quantity}
                      availableQuantity={groupedAsset.total_available_quantity}
                      onChange={quantity =>
                        updateKitAsset(kitAsset.id, { assetId: kitAsset.asset.id, quantity })
                      }
                    />
                  ) : (
                    groupedAsset.required_quantity
                  )}
                </TableCell>
                <TableCell align="center" style={{ background }}>
                  {groupedAsset.total_available_quantity}
                </TableCell>
                {!disabled && editMode && (
                  <TableCell align="right" style={{ background }}>
                    {kitAsset && (
                      <IconButton color="inherit" onClick={() => setItemToRemove(kitAsset.id)}>
                        <Tooltip arrow title="Remove">
                          <HighlightOffIcon />
                        </Tooltip>
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <Paper>
      <Box p={2}>
        <Typography fontWeight={500} fontSize={18}>
          Kit Builder
        </Typography>
        <Box my={2}>
          {loading ? (
            <Box p={3} borderRadius="4px" display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : kit?.kitAssets?.length ? (
            isGroupingEnabled ? (
              // Show grouped view when toggle is enabled
              renderGroupedView()
            ) : (
              // Show normal view when toggle is disabled
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Image</TableCell>
                    <TableCell align="center">Item ID</TableCell>
                    <TableCell align="center">Description</TableCell>
                    {showModelColumn && <TableCell align="center">Model</TableCell>}
                    {showDimensionsColumn && <TableCell align="center">Dimensions</TableCell>}
                    <TableCell align="center">Required Quantity</TableCell>
                    <TableCell align="center">{user.company.settings.quantity_label}</TableCell>
                    {!disabled && editMode && <TableCell align="center" />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kit?.kitAssets?.map(({ asset, ...item }, index) => {
                    const photo = getImage(asset);
                    const highlight = item.quantity > asset?.availableQuantity;
                    const background = highlight ? "#fffbf2" : undefined;
                    return (
                      <TableRow key={index} style={{ width: 90 }}>
                        <TableCell align="center" style={{ background }}>
                          {photo ? (
                            <img style={{ maxWidth: 90, maxHeight: 60 }} src={photo} alt="" />
                          ) : (
                            "No image"
                          )}
                        </TableCell>
                        <TableCell align="center" style={{ background }}>
                          <ItemId asset={asset} />
                        </TableCell>
                        <TableCell align="center" style={{ background }}>
                          {asset.description}
                        </TableCell>
                        {showModelColumn && (
                          <TableCell align="center" style={{ background }}>
                            {hasModel(asset) ? asset.model : "—"}
                          </TableCell>
                        )}
                        {showDimensionsColumn && (
                          <TableCell align="center" style={{ background }}>
                            <Dimensions dimensions={asset.dimensions} />
                          </TableCell>
                        )}
                        <TableCell align="center" style={{ background }}>
                          <KitAssetQuantity
                            disabled={disabled || !editMode}
                            value={item.quantity}
                            availableQuantity={asset.availableQuantity}
                            onChange={quantity =>
                              updateKitAsset(item.id, { assetId: asset.id, quantity })
                            }
                          />
                        </TableCell>
                        <TableCell align="center" style={{ background }}>
                          {asset?.availableQuantity}
                        </TableCell>
                        {!disabled && editMode && (
                          <TableCell align="right" style={{ background }}>
                            <IconButton color="inherit" onClick={() => setItemToRemove(item.id)}>
                              <Tooltip arrow title="Remove">
                                <HighlightOffIcon />
                              </Tooltip>
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )
          ) : (
            <Box p={3} bgcolor="#f4f6f8" borderRadius="4px" display="flex" justifyContent="center">
              Empty
            </Box>
          )}
        </Box>
        {!disabled && (
          <>
            {kit?.kitAssets?.length === 0 ? (
              <Button variant="outlined" onClick={handleToggle}>
                Add Component
              </Button>
            ) : (
              <>
                {editMode ? (
                  <Button variant="outlined" onClick={handleToggle}>
                    Add Component
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                  >
                    Edit Mode
                  </Button>
                )}
              </>
            )}
          </>
        )}
        {query.get("kitId") === entity.id && (
          <AddComponentModal onClose={handleToggle} projectId={projectId} />
        )}
        {itemToRemove && (
          <ConfirmDeleteModal
            title="Remove Component"
            descr="Are you sure you want to remove this component from the kit?"
            onClose={handleCloseRemoveModal}
            onDelete={handleConfirmRemove}
          />
        )}
      </Box>
    </Paper>
  );
}
